import React, { useEffect, useState, useCallback } from 'react';
import { Users, Home, CheckCircle2, TrendingUp, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import communityRegistryApi, {
  ChwPerformanceSummary,
  ChwPerformanceRow,
} from '../../services/communityRegistryApi';

const STATUS_BADGE: Record<string, string> = {
  High: 'bg-emerald-100 text-emerald-700',
  Average: 'bg-amber-100 text-amber-700',
  'Needs Support': 'bg-red-100 text-red-700',
};

const SUMMARY_DEFS: Array<{
  key: keyof ChwPerformanceSummary;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  format?: (v: number) => string;
}> = [
  { key: 'total_chws_active', label: 'Active CHWs', icon: Users },
  { key: 'households_visited', label: 'Households Visited', icon: Home },
  { key: 'services_provided', label: 'Services Provided', icon: CheckCircle2 },
  {
    key: 'average_coverage_pct',
    label: 'Average Coverage',
    icon: TrendingUp,
    format: (v) => `${v.toFixed(0)}%`,
  },
];

const CoverageBar: React.FC<{ pct: number }> = ({ pct }) => {
  const clamped = Math.max(0, Math.min(100, pct));
  const color = clamped >= 80 ? 'bg-emerald-500' : clamped >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${clamped}%` }} />
      </div>
      <span className="text-xs text-muted-foreground">{clamped.toFixed(0)}%</span>
    </div>
  );
};

const CHWPerformanceTab: React.FC = () => {
  const [summary, setSummary] = useState<ChwPerformanceSummary | null>(null);
  const [rows, setRows] = useState<ChwPerformanceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      communityRegistryApi.chwPerformanceSummary(),
      communityRegistryApi.chwPerformanceList(page, pageSize),
    ])
      .then(([summaryRes, listRes]) => {
        setSummary(summaryRes);
        setRows(listRes.items);
        setTotal(listRes.total);
        setError(null);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load CHW performance'))
      .finally(() => setLoading(false));
  }, [page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SUMMARY_DEFS.map(({ key, label, icon: Icon, format }) => (
          <Card key={key}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
              <Icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading || !summary ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <div className="text-2xl font-bold">
                  {format ? format(summary[key] as number) : summary[key]}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>CHW performance</CardTitle>
          <a href={communityRegistryApi.chwPerformanceExportUrl()} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </a>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>CHW</TableHead>
                <TableHead>Aire / FOSA</TableHead>
                <TableHead className="text-right">Assigned</TableHead>
                <TableHead className="text-right">Visited</TableHead>
                <TableHead>Coverage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Services</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                    No CHW performance data yet.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => (
                  <TableRow key={row.chw_id}>
                    <TableCell className="font-medium">{row.name || row.chw_code}</TableCell>
                    <TableCell>{row.aire_fosa || '—'}</TableCell>
                    <TableCell className="text-right">{row.households_assigned}</TableCell>
                    <TableCell className="text-right">{row.households_visited}</TableCell>
                    <TableCell>
                      <CoverageBar pct={row.coverage_pct} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_BADGE[row.performance_status] || ''}>
                        {row.performance_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{row.services_provided}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <div>
              {total > 0
                ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`
                : 'No results'}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span>
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CHWPerformanceTab;
